// shared/src/oracle/macro-series-store.ts
// D1 store for oracle_macro_series (migrations/0102_oracle_macro_series.sql) — the per-FIELD
// bitemporal decomposition of the macro block (data-plane Slice 2). Writer + row reader + Row type
// + Zod live in this ONE module (migration-lockstep gate; mirrors oracle-store.ts). The gateway's
// ASSEMBLED read (readMacroBlock, shared/src/dataplane/d1-readers.ts) reassembles the identical
// computed.macro block from these rows as-of a clock.
//
// Encoding: the macro block is scalar-only (number | string | null — verified against
// buildFredMacroBlock + buildMacroGroupC). Each field → exactly one of value_num / value_text
// populated, or BOTH null for a present-but-null field. decode is the exact inverse, so
// block → rows → block' round-trips byte-for-byte (proven in the Slice-2 test).

import { z } from 'zod';
import { FRED_FIELD_SERIES } from './macro-block.js';
import type { OracleD1 } from './oracle-store.js';

export interface OracleMacroSeriesRow {
  readonly id:          string;   // `${run_id}:${field}`
  readonly run_id:      string;
  readonly field:       string;   // computed.macro key
  readonly value_num:   number | null;
  readonly value_text:  string | null;
  readonly observed_at: string;   // world-truth instant (ISO-Z)
  readonly created_at:  string;   // write instant (ISO-Z)
  // #79 DISPLAY-ONLY provenance (migration 0110) — joined from the fire's authoritative resolution
  // for FRED-derived fields; NULL for Group-C (single-source) fields. Never feeds the LLM.
  readonly issuer:          string | null;   // resolved issuer source_id (used[0])
  readonly status:          string | null;   // resolveAuthoritative status (ok|below_quorum|no_candidates)
  readonly n_corroborators: number | null;   // used.length - 1
}
export const OracleMacroSeriesRowSchema = z.object({
  id:          z.string(),
  run_id:      z.string(),
  field:       z.string(),
  value_num:   z.number().nullable(),
  value_text:  z.string().nullable(),
  observed_at: z.string(),
  created_at:  z.string(),
  issuer:          z.string().nullable(),
  status:          z.string().nullable(),
  n_corroborators: z.number().nullable(),
});
const MACRO_SERIES_COLS = 'id, run_id, field, value_num, value_text, observed_at, created_at, issuer, status, n_corroborators';

/** Per-field display provenance carried on a series row (#79). */
export interface MacroFieldProvenance {
  readonly issuer:          string | null;
  readonly status:          string | null;
  readonly n_corroborators: number | null;
}

/** Join the macro fire's per-FRED-series authoritative resolution onto the block FIELDS that derive
 *  from each series (1:1 field→series via FRED_FIELD_SERIES). A Group-C field is absent from the map
 *  → no entry → NULL provenance (single-source, never routed through resolveAuthoritative). Pure. */
export function macroProvenanceByField(
  resultsBySeriesId: Readonly<Record<string, { status: string; used: ReadonlyArray<{ source_id: string }> }>>,
): Record<string, MacroFieldProvenance> {
  const out: Record<string, MacroFieldProvenance> = {};
  for (const [field, seriesId] of Object.entries(FRED_FIELD_SERIES)) {
    const r = resultsBySeriesId[seriesId];
    if (r === undefined) continue;
    out[field] = {
      issuer:          r.used[0]?.source_id ?? null,
      status:          r.status,
      n_corroborators: Math.max(0, r.used.length - 1),
    };
  }
  return out;
}

/** Encode a scalar macro field value into the typed (value_num, value_text) pair. Fail-loud on a
 *  non-scalar — the block is scalar-only by construction, so an object/array is a producer bug, not
 *  something to silently coerce (feedback_no_fallbacks). */
export function encodeMacroField(v: unknown): { value_num: number | null; value_text: string | null } {
  if (v === null || v === undefined) return { value_num: null, value_text: null };
  if (typeof v === 'number') return { value_num: v, value_text: null };
  if (typeof v === 'string') return { value_num: null, value_text: v };
  throw new Error(`encodeMacroField: non-scalar macro field value (${typeof v}) — the macro block must be number|string|null`);
}

/** Decode a stored (value_num, value_text) pair back to the original scalar. Exact inverse of
 *  encodeMacroField: exactly one is populated (→ that value), or both null (→ null). */
export function decodeMacroField(value_num: number | null, value_text: string | null): number | string | null {
  if (value_num !== null) return value_num;
  if (value_text !== null) return value_text;
  return null;
}

/** Write one row per PRESENT block field (idempotent INSERT OR IGNORE, deterministic id). Preserves
 *  the exact key set: a present-with-null field gets a both-null row; an absent field (e.g. a
 *  Group-C vendor that failed) gets NO row, so the reassembled block omits it too — matching the
 *  block the producer built. */
export async function writeMacroSeries(
  db: OracleD1,
  args: {
    runId: string; block: Readonly<Record<string, unknown>>; observedAt: string; createdAt: string;
    // #79: per-field display provenance (issuer/status/n_corroborators). A field absent from the map
    // (every Group-C field) is written with NULL provenance.
    provenanceByField?: Readonly<Record<string, MacroFieldProvenance>>;
  },
): Promise<void> {
  const { runId, block, observedAt, createdAt, provenanceByField } = args;
  const fields = Object.keys(block);
  if (fields.length === 0) return;
  const stmts = fields.map((field) => {
    const { value_num, value_text } = encodeMacroField(block[field]);
    const p = provenanceByField?.[field];
    return db
      .prepare(`INSERT OR IGNORE INTO oracle_macro_series (${MACRO_SERIES_COLS}) VALUES (?,?,?,?,?,?,?,?,?,?)`)
      .bind(
        `${runId}:${field}`, runId, field, value_num, value_text, observedAt, createdAt,
        p?.issuer ?? null, p?.status ?? null, p?.n_corroborators ?? null,
      );
  });
  await db.batch(stmts);
}

/** The latest macro fire whose created_at <= asOfIso (its run_id + timestamps), or null if none. */
export async function readLatestMacroSeriesRun(
  db: OracleD1,
  asOfIso: string,
): Promise<{ run_id: string; observed_at: string; created_at: string } | null> {
  return db
    .prepare(
      `SELECT run_id, observed_at, created_at FROM oracle_macro_series
        WHERE created_at <= ? ORDER BY created_at DESC LIMIT 1`,
    )
    .bind(asOfIso)
    .first<{ run_id: string; observed_at: string; created_at: string }>();
}

/** Per-FRED-FIELD last-good: the most recent NON-NULL value (created_at <= asOfIso) of each field in
 *  FRED_FIELD_SERIES, decoded to a scalar. Returns the FULL FRED key set (every field present; a field
 *  with no non-null history → null) so the reassembled block preserves buildFredMacroBlock's key set
 *  exactly. Used by the oracle macro fire to carry FRED forward on a tick where FRED was NOT freshly
 *  fetched (poll-not-due prefire, the #109 breaker paused, or an all-null burst): the block serves the
 *  last KNOWN value of each series instead of the nulls an empty fetch would produce
 *  (feedback_vendor_failclose_preserve_lastgood). This is a fail-soft, NOT a claim the data is
 *  slow-moving — ~8 of the 19 series are DAILY (treasury yields, HY spread, Brent) and refresh on the
 *  next successful fetch; carrying them forward through an outage beats nulling them. Group-C fields are
 *  NOT included (they are re-fetched fresh every fire). */
export async function readLatestGoodFredBlockFields(
  db: OracleD1,
  asOfIso: string,
): Promise<Record<string, number | string | null>> {
  const fredFields = Object.keys(FRED_FIELD_SERIES);
  const out: Record<string, number | string | null> = {};
  for (const field of fredFields) out[field] = null; // full key set, null until a last-good row overrides
  if (fredFields.length === 0) return out;
  // Migration 0130: the old `ROW_NUMBER() OVER (PARTITION BY field …)` windowed the whole macro
  // partition (~56k rows) + a temp b-tree on every macro fire. Rewritten to a per-field correlated
  // `id = (… ORDER BY created_at DESC LIMIT 1)` seek on idx_oracle_macro_series_field
  // (field, created_at) WHERE value present: ONE index seek per FRED field ⇒ O(#fields) rows read,
  // no temp b-tree. Same `(value_num IS NOT NULL OR value_text IS NOT NULL)` last-good skip and
  // `created_at <= asOfIso` as-of pin.
  const fs = fredFields.map(() => '(?)').join(',');
  const { results } = await db
    .prepare(
      `WITH fs(f) AS (VALUES ${fs}) `
      + `SELECT m.field AS field, m.value_num AS value_num, m.value_text AS value_text`
      + ` FROM oracle_macro_series m WHERE m.id IN (`
      + `SELECT (SELECT m2.id FROM oracle_macro_series m2`
      + ` WHERE m2.field = fs.f AND (m2.value_num IS NOT NULL OR m2.value_text IS NOT NULL) AND m2.created_at <= ?`
      + ` ORDER BY m2.created_at DESC LIMIT 1) FROM fs)`,
    )
    .bind(...fredFields, asOfIso)
    .all<{ field: string; value_num: number | null; value_text: string | null }>();
  for (const r of results) out[r.field] = decodeMacroField(r.value_num, r.value_text);
  return out;
}

/** All field rows for one macro fire. */
export async function readMacroSeriesRows(db: OracleD1, runId: string): Promise<OracleMacroSeriesRow[]> {
  const { results } = await db
    .prepare(`SELECT ${MACRO_SERIES_COLS} FROM oracle_macro_series WHERE run_id = ?`)
    .bind(runId)
    .all<OracleMacroSeriesRow>();
  return results;
}

/** Per-fire aggregate for the /oracle MACRO tab's Runs panel (#79 slice 2): each recent macro fire
 *  with its field count + how many fields carried a value (value_num OR value_text non-null). */
export interface MacroSeriesRunAgg {
  readonly run_id:       string;
  readonly created_at:   string;
  readonly series_total: number;
  readonly series_ok:    number;
}
export async function readRecentMacroSeriesRuns(db: OracleD1, limit: number): Promise<MacroSeriesRunAgg[]> {
  // Migration 0130: the old `GROUP BY run_id` scanned + temp-b-tree-sorted the whole macro
  // partition (~56k rows) on every /oracle/macro load. Rewritten as a bounded distinct-run walk:
  // a fire writes all its fields with ONE created_at, so the `limit` newest DISTINCT created_at
  // values (skip-scan on idx_oracle_macro_series_created) bound a range fetch, aggregated per
  // run_id in JS. No temp b-tree, no full partition scan.
  const floorRes = await db
    .prepare(`SELECT DISTINCT created_at FROM oracle_macro_series ORDER BY created_at DESC LIMIT ?`)
    .bind(limit)
    .all<{ created_at: string }>();
  const floors = (floorRes.results ?? []).map((r) => r.created_at);
  if (floors.length === 0) return [];
  const floor = floors[floors.length - 1]!;
  const { results } = await db
    .prepare(
      `SELECT run_id, value_num, value_text, created_at FROM oracle_macro_series`
      + ` WHERE created_at >= ? ORDER BY created_at DESC`,
    )
    .bind(floor)
    .all<{ run_id: string; value_num: number | null; value_text: string | null; created_at: string }>();
  const agg = new Map<string, { run_id: string; created_at: string; series_total: number; series_ok: number }>();
  for (const r of results ?? []) {
    let a = agg.get(r.run_id);
    if (!a) { a = { run_id: r.run_id, created_at: r.created_at, series_total: 0, series_ok: 0 }; agg.set(r.run_id, a); }
    if (r.created_at > a.created_at) a.created_at = r.created_at;
    a.series_total += 1;
    if (r.value_num !== null || r.value_text !== null) a.series_ok += 1;
  }
  return [...agg.values()]
    .sort((x, y) => y.created_at.localeCompare(x.created_at))
    .slice(0, limit);
}
